import type { ApprovalWorkflow, CreateWorkflowRequest, UpdateWorkflowRequest, WorkflowAuditLog, ValidationResult } from '@/types/workflow';
import {
  MOCK_WORKFLOWS,
  getMockWorkflowById,
  getMockWorkflowHistory,
  createMockWorkflow,
  updateMockWorkflow,
  deleteMockWorkflow,
} from '@/data/mockWorkflows';
import { apiClient } from './apiClient';

const USE_MOCK_DATA = false; // Set to true to use mock data for development

export async function getAllWorkflows(activeOnly: boolean = false): Promise<ApprovalWorkflow[]> {
  if (USE_MOCK_DATA) {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));
    
    if (activeOnly) {
      return MOCK_WORKFLOWS.filter((w) => w.isActive);
    }
    return [...MOCK_WORKFLOWS];
  }

  const endpoint = activeOnly ? '/workflows?activeOnly=true' : '/workflows';
  return await apiClient.get<ApprovalWorkflow[]>(endpoint);
}

export async function getWorkflowById(id: string): Promise<ApprovalWorkflow> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    
    const workflow = getMockWorkflowById(id);
    if (!workflow) {
      throw new Error('Workflow not found');
    }
    return workflow;
  }

  return await apiClient.get<ApprovalWorkflow>(`/workflows/${id}`);
}

export async function createWorkflow(request: CreateWorkflowRequest): Promise<ApprovalWorkflow> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    
    const workflow = createMockWorkflow({
      name: request.name,
      description: request.description,
      isActive: request.isActive,
      isDefault: request.isDefault,
      stages: request.stages as any,
    });
    
    return workflow;
  }

  const data = await apiClient.post<{ workflow: ApprovalWorkflow }>('/workflows', request);
  return data.workflow;
}

export async function updateWorkflow(
  id: string,
  request: UpdateWorkflowRequest
): Promise<ApprovalWorkflow> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    
    const workflow = updateMockWorkflow(id, {
      name: request.name,
      description: request.description,
      isActive: request.isActive,
      isDefault: request.isDefault,
      stages: request.stages as any,
    });
    
    if (!workflow) {
      throw new Error('Workflow not found');
    }
    
    return workflow;
  }

  const data = await apiClient.put<{ workflow: ApprovalWorkflow }>(`/workflows/${id}`, request);
  return data.workflow;
}

export async function deleteWorkflow(id: string): Promise<void> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    
    const success = deleteMockWorkflow(id);
    if (!success) {
      const workflow = getMockWorkflowById(id);
      if (!workflow) {
        throw new Error('Workflow not found');
      }
      if (workflow.isInUse) {
        throw new Error('Cannot delete workflow that is in use');
      }
      if (workflow.isDefault) {
        throw new Error('Cannot delete default workflow');
      }
    }
    return;
  }

  await apiClient.delete<void>(`/workflows/${id}`);
}

export async function activateWorkflow(id: string): Promise<ApprovalWorkflow> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    
    const workflow = updateMockWorkflow(id, { isActive: true });
    if (!workflow) {
      throw new Error('Workflow not found');
    }
    return workflow;
  }

  const data = await apiClient.post<{ workflow: ApprovalWorkflow }>(`/workflows/${id}/activate`, {});
  return data.workflow;
}

export async function deactivateWorkflow(id: string): Promise<{ workflow: ApprovalWorkflow; unassignedCount: number }> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    
    const workflow = updateMockWorkflow(id, { isActive: false });
    if (!workflow) {
      throw new Error('Workflow not found');
    }
    return { workflow, unassignedCount: 0 };
  }

  const data = await apiClient.post<{ workflow: ApprovalWorkflow; unassignedCount: number }>(`/workflows/${id}/deactivate`, {});
  return data;
}

export async function setDefaultWorkflow(id: string): Promise<ApprovalWorkflow> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    
    // First, unset all other defaults
    MOCK_WORKFLOWS.forEach((w) => {
      if (w.isDefault) {
        w.isDefault = false;
      }
    });
    
    // Then set this one as default
    const workflow = updateMockWorkflow(id, { isDefault: true });
    if (!workflow) {
      throw new Error('Workflow not found');
    }
    return workflow;
  }

  const data = await apiClient.post<{ workflow: ApprovalWorkflow }>(`/workflows/${id}/set-default`, {});
  return data.workflow;
}

export async function getWorkflowHistory(id: string): Promise<WorkflowAuditLog[]> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    
    return getMockWorkflowHistory(id);
  }

  return await apiClient.get<WorkflowAuditLog[]>(`/workflows/${id}/history`);
}

export async function validateWorkflow(request: CreateWorkflowRequest): Promise<ValidationResult> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    
    const errors: string[] = [];
    
    // Basic validation
    if (!request.name || request.name.trim().length === 0) {
      errors.push('Workflow name is required');
    }
    
    if (!request.stages || request.stages.length === 0) {
      errors.push('At least one stage is required');
    }
    
    if (request.stages) {
      request.stages.forEach((stage, index) => {
        if (!stage.name || stage.name.trim().length === 0) {
          errors.push(`Stage ${index + 1}: Name is required`);
        }
        
        if (!stage.approvers || stage.approvers.length === 0) {
          errors.push(`Stage ${index + 1}: At least one approver is required`);
        }
        
        if (stage.approvalType === 'majority' && stage.requiredApprovals < 2) {
          errors.push(`Stage ${index + 1}: Majority approval requires at least 2 approvers`);
        }
      });
    }
    
    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  return await apiClient.post<ValidationResult>('/workflows/validate', request);
}

export interface AssignWorkflowRequest {
  workflowId: string;
  assignmentType: 'all' | 'selected';
  employeeIds?: number[];
}

export interface AssignWorkflowResponse {
  success: boolean;
  assignedCount: number;
  message: string;
}

export async function assignWorkflow(
  workflowId: string,
  assignmentType: 'all' | 'selected',
  employeeIds?: number[]
): Promise<AssignWorkflowResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    
    // Mock response
    const assignedCount = assignmentType === 'all' 
      ? 50 // Mock: assume 50 users in system
      : employeeIds?.length || 0;
    
    return {
      success: true,
      assignedCount,
      message: `Workflow assigned to ${assignedCount} user(s) successfully`,
    };
  }

  const request: AssignWorkflowRequest = {
    workflowId,
    assignmentType,
    employeeIds: assignmentType === 'selected' ? employeeIds : undefined,
  };

  return await apiClient.post<AssignWorkflowResponse>(`/workflows/${workflowId}/assign`, request);
}

export interface DuplicateWorkflowOptions {
  copyStages: boolean;
  copyRules: boolean;
  copyAssignments: boolean;
}

export async function duplicateWorkflow(
  workflowId: string,
  newName: string,
  options: DuplicateWorkflowOptions
): Promise<ApprovalWorkflow> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    
    // Get the source workflow
    const sourceWorkflow = getMockWorkflowById(workflowId);
    if (!sourceWorkflow) {
      throw new Error('Source workflow not found');
    }
    
    // Create a copy
    const duplicatedWorkflow = createMockWorkflow({
      name: newName,
      description: sourceWorkflow.description,
      isActive: false, // Always create as inactive/draft
      isDefault: false,
      stages: options.copyStages ? sourceWorkflow.stages.map((stage) => ({
        ...stage,
        id: `stage-${Date.now()}-${Math.random()}`,
        // Remove rules if not copying them
        advancedConfigJson: options.copyRules ? stage.advancedConfigJson : undefined,
        automationRules: options.copyRules ? stage.automationRules : undefined,
      })) : [],
    });
    
    return duplicatedWorkflow;
  }

  const data = await apiClient.post<{ workflow: ApprovalWorkflow }>(
    `/workflows/${workflowId}/duplicate`,
    {
      newName,
      ...options,
    }
  );
  return data.workflow;
}
